import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import MobileNav from "@/components/MobileNav";
import {
  ArrowLeft,
  Send,
  Palette,
  Image,
  Settings,
  Edit,
  Check,
  AlertCircle,
  Eye,
  Sparkles,
  ChevronRight,
  Camera,
  Accessibility,
  MessageSquare
} from "lucide-react";
import { toast } from "sonner";

const BoardReviewPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [board, setBoard] = useState(null);
  const [preferences, setPreferences] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [customerNotes, setCustomerNotes] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [boardRes, prefsRes] = await Promise.all([
        axios.get(`${API}/board/current`, { withCredentials: true }),
        axios.get(`${API}/preferences`, { withCredentials: true })
      ]);
      setBoard(boardRes.data);
      setPreferences(prefsRes.data);
      setCustomerNotes(boardRes.data?.customer_notes || "");
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 404) {
        navigate('/wallet');
      }
    } finally {
      setLoading(false);
    }
  };

  const submitBoard = async () => {
    setSubmitting(true);
    try {
      // Save notes first
      if (customerNotes !== board?.customer_notes) {
        await axios.put(`${API}/board/${board.board_id}`, 
          { customer_notes: customerNotes },
          { withCredentials: true }
        );
      }
      
      // Lock the board (this sends it to the tech)
      await axios.post(`${API}/board/${board.board_id}/lock`, {}, { withCredentials: true });
      toast.success("Your appointment has been sent to your technician!");
      navigate('/wallet');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not submit appointment");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-black border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!board) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-12 h-12 text-gray-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">No Active Appointment</h2>
            <p className="text-gray-700 mb-4">Check in to a salon to start building your appointment.</p>
            <Link to="/wallet">
              <Button className="rounded-full">Go to Wallet</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const manicurePrefs = preferences?.manicure || {};
  const accessibilityPrefs = preferences?.accessibility || {};

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-lg mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button onClick={() => navigate('/board')} className="p-2 -ml-2 hover:bg-gray-100 rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="font-display text-lg font-semibold">Review and Send</h1>
            <div className="w-9" />
          </div>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6 space-y-6">
        {/* What Your Tech Sees Banner */}
        <div className="bg-gradient-to-r from-gray-900 to-black rounded-2xl p-5 text-white">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
              <Eye className="w-5 h-5" />
            </div>
            <div>
              <h2 className="font-semibold">What Your Tech Will See</h2>
              <p className="text-sm text-gray-300">Review before sending</p>
            </div>
          </div>
        </div>

        {/* Selected Colors */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Palette className="w-4 h-4 text-pink-500" />
                Colors
                <span className="text-sm font-normal text-gray-600">
                  ({board.selected_colors?.length || 0})
                </span>
              </CardTitle>
              <Link to="/colors">
                <Button variant="ghost" size="sm" className="text-xs">
                  <Edit className="w-3 h-3 mr-1" /> Edit
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {board.selected_colors?.length > 0 ? (
              <div className="space-y-2">
                {board.selected_colors.map((color) => (
                  <div key={color.color_id} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                    <div 
                      className="w-10 h-10 rounded-lg shadow-inner border border-gray-200" 
                      style={{ backgroundColor: color.hex_code }} 
                    />
                    <div className="flex-1">
                      <p className="font-medium text-sm">{color.name}</p>
                      <p className="text-xs text-gray-600">{color.brand} • {color.product_code}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-600 text-sm py-2">No colors selected</p>
            )}
          </CardContent>
        </Card>

        {/* Selected Designs */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Image className="w-4 h-4 text-purple-500" />
                Designs
                <span className="text-sm font-normal text-gray-600">
                  ({board.selected_designs?.length || 0})
                </span>
              </CardTitle>
              <Link to="/portfolio">
                <Button variant="ghost" size="sm" className="text-xs">
                  <Edit className="w-3 h-3 mr-1" /> Edit
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {board.selected_designs?.length > 0 ? (
              <div className="grid grid-cols-3 gap-2">
                {board.selected_designs.map((design) => (
                  <div key={design.portfolio_id} className="aspect-square rounded-lg overflow-hidden">
                    <img 
                      src={design.image_url} 
                      alt={design.title} 
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-600 text-sm py-2">No designs selected</p>
            )}
          </CardContent>
        </Card>

        {/* Inspiration Photos */}
        {board.inspiration_images?.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <Camera className="w-4 h-4 text-amber-500" />
                  My Inspirations
                  <span className="text-sm font-normal text-gray-600">
                    ({board.inspiration_images.length})
                  </span>
                </CardTitle>
                <Link to="/inspirations">
                  <Button variant="ghost" size="sm" className="text-xs">
                    <Edit className="w-3 h-3 mr-1" /> Edit
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-2">
                {board.inspiration_images.slice(0, 6).map((img, idx) => (
                  <div key={idx} className="aspect-square rounded-lg overflow-hidden">
                    <img 
                      src={img.url || img} 
                      alt="Inspiration" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Preferences */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Settings className="w-4 h-4 text-gray-600" />
                Preferences
              </CardTitle>
              <Link to="/preferences">
                <Button variant="ghost" size="sm" className="text-xs">
                  <Edit className="w-3 h-3 mr-1" /> Edit
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3 text-sm">
              {manicurePrefs.shape && manicurePrefs.shape !== "None" && (
                <div className="flex items-center gap-2">
                  <span className="text-gray-600">Shape:</span>
                  <span className="font-medium">{manicurePrefs.shape}</span>
                </div>
              )}
              {manicurePrefs.length && manicurePrefs.length !== "None" && (
                <div className="flex items-center gap-2">
                  <span className="text-gray-600">Length:</span>
                  <span className="font-medium">{manicurePrefs.length}</span>
                </div>
              )}
              {manicurePrefs.finish && manicurePrefs.finish !== "None" && (
                <div className="flex items-center gap-2">
                  <span className="text-gray-600">Finish:</span>
                  <span className="font-medium">{manicurePrefs.finish}</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Accessibility */}
        {(accessibilityPrefs.mobility || accessibilityPrefs.sensory || accessibilityPrefs.invisible_disabilities) && (
          <Card className="border-green-200 bg-green-50/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2 text-green-700">
                <Accessibility className="w-4 h-4" />
                Accessibility Notes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                {accessibilityPrefs.mobility && accessibilityPrefs.mobility !== "None" && (
                  <div className="flex items-center gap-2">
                    <span className="text-green-600">Mobility:</span>
                    <span className="font-medium text-green-800">{accessibilityPrefs.mobility}</span>
                  </div>
                )}
                {accessibilityPrefs.sensory && accessibilityPrefs.sensory !== "None" && (
                  <div className="flex items-center gap-2">
                    <span className="text-green-600">Sensory:</span>
                    <span className="font-medium text-green-800">{accessibilityPrefs.sensory}</span>
                  </div>
                )}
                {accessibilityPrefs.invisible_disabilities && accessibilityPrefs.invisible_disabilities !== "None" && (
                  <div className="flex items-center gap-2">
                    <span className="text-green-600">Note:</span>
                    <span className="font-medium text-green-800">{accessibilityPrefs.invisible_disabilities}</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Customer Notes */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-blue-500" />
              Notes for Your Tech
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              placeholder="Anything else you'd like your technician to know? (e.g., 'Going to a wedding this weekend!' or 'Please be gentle, my nails are sensitive')"
              value={customerNotes}
              onChange={(e) => setCustomerNotes(e.target.value)}
              className="min-h-[80px] text-sm"
            />
          </CardContent>
        </Card>

        {/* Submit Button */}
        <div className="pt-4">
          <Button 
            onClick={submitBoard}
            disabled={submitting}
            className="w-full h-14 rounded-2xl bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white text-lg font-semibold shadow-lg"
          >
            {submitting ? (
              <>
                <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full mr-2" />
                Sending...
              </>
            ) : (
              <>
                <Send className="w-5 h-5 mr-2" />
                Send to My Technician
              </>
            )}
          </Button>
          <p className="text-center text-xs text-gray-600 mt-3">
            Your technician will be notified when you submit
          </p>
        </div>
      </main>

      <MobileNav />
    </div>
  );
};

export default BoardReviewPage;
